import React, { HTMLAttributes, useMemo } from 'react';

import { format } from 'date-fns';

import { Text } from '@components/common';
import { ExportCsvButton } from '@components/common/ExportCsvButton';
import { getTableColumnDefs } from '@components/ScreenerResults/components/CardSortResponse/components/helpers/getTableColumnDefs';
import { TABLE_PAGE_SIZE } from '@components/ScreenerResults/constants';
import { Paginator } from '@components/shared/Paginator';
import { Table } from '@components/shared/Table';
import { usePagination } from '@components/shared/Table/hooks';
import { Tooltip } from '@components/Tooltip';
import { track } from '@components/tracking';
import { usePermission } from '@hooks/usePermission';

import { CSV_HEADERS } from '../constants';
import { getCsvDataForResponsesTable } from '../utils';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  answers: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>[];
  setPreview: (answers: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>) => void;
  blockableId: number;
}

export const ResponsesTable: React.FC<React.PropsWithChildren<Props>> = ({ answers, setPreview, blockableId }) => {
  const { page, nextPage, previousPage, totalPages } = usePagination({
    pageSize: TABLE_PAGE_SIZE,
    dataLength: answers.length
  });

  const columns = useMemo(() => getTableColumnDefs({ setPreview }), []);

  const isAdmin = usePermission('isAdmin')();

  const tableFilename = `card_sort_${blockableId}_responses_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

  const tableCsvData = useMemo(() => {
    return getCsvDataForResponsesTable(answers);
  }, [answers]);

  return (
    <div className='w-full'>
      <div className='mb-1 flex h-8 items-center justify-between'>
        <div className='flex items-center justify-between'>
          <Text bold>Results table</Text>
        </div>

        <div className='flex items-center space-x-2'>
          <Tooltip content='Export results table to CSV' isDisabled={!isAdmin}>
            <ExportCsvButton
              onClick={() => track('export_overall_metrics_csv_button_clicked', { type: 'card_sort', tab: 'table' })}
              medium
              disabled={!isAdmin}
              label=''
              aria-label='Export results table to CSV'
              data={tableCsvData}
              headers={CSV_HEADERS}
              filename={tableFilename}
              disabledTooltip='Only admin users may export Survey results'
            />
          </Tooltip>
          <Paginator
            canClickPrevious={page > 0}
            onClickPrevious={previousPage}
            canClickNext={page < totalPages - 1}
            onClickNext={nextPage}
            current={page + 1}
            total={totalPages}
          />
        </div>
      </div>
      <div className='relative max-w-full overflow-auto'>
        <Table
          pageSize={TABLE_PAGE_SIZE}
          currentPage={page}
          columnPinning={{ left: ['participation_name'] }}
          data={answers}
          columns={columns}
          className='w-full table-fixed border border-b-0 border-gray-200 bg-white'
        />
      </div>
    </div>
  );
};
